import {
  Typography,
  Button,
  Box,
  LinearProgress,
  Paper,
  CircularProgress,
} from "@mui/material";
import { ContactSupport, Language } from "@mui/icons-material";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate } from "react-router-dom";
import EvStationIcon from "@mui/icons-material/EvStation";
import { sidebarStyles } from "../components/styles/deviceControlStyles";
import CleaningLevelSlider from "./CleaningLevelSlider";

// Smart docking button logic
function getDockButtonConfig(robotStatus, isDriveToDockinProgress, t) {
  const isCurrentlyDocking =
    isDriveToDockinProgress || robotStatus.state === "returning";
  // On the station: charging, station work (mop washing, dust emptying) or mop
  // drying. Sending it to the dock makes no sense there, so the button is disabled.
  const isOnDock = ["charging", "charged", "docked", "busy", "drying"].includes(
    robotStatus.state,
  );

  return {
    disabled: !isCurrentlyDocking && isOnDock, // Only disable when already at dock
    text: isCurrentlyDocking ? t("cancelDocking") : t("driveToDock"),
    action: isCurrentlyDocking ? "cancel" : "dock",
    variant: isCurrentlyDocking ? "contained" : "contained",
    color: isCurrentlyDocking ? "secondary" : "primary",
  };
}

const ControlSidebar = ({
  user,
  language,
  toggleLanguage,
  zones,
  robotStatus,
  isDriveToDockinProgress,
  handleDriveToDock,
  handleCancelDocking, // New prop for cancel action
  handleContactSupport,
  handleSettings,
  supportSentForCurrentError,
  deviceStatus,
  sseConnected,
  t,
  cleaningLevel,
  setCleaningLevel,
  cleaningConfigPresent,
  cleaningPresets = [],
  guidePath,
}) => {
  const navigate = useNavigate();

  const DISPLAY_GUIDE = false; // Set to true to show new guide feature

  // Cleaning preset slider: position 1..n = presets in display order
  const presetIndex = cleaningPresets.findIndex(
    (preset) => Number(preset.cleaning_level) === Number(cleaningLevel),
  );
  const presetPosition = presetIndex >= 0 ? presetIndex + 1 : 1;
  const presetLabels = cleaningPresets.reduce((labels, preset, index) => {
    labels[index + 1] =
      language === "da"
        ? preset.frontend_name_dk || preset.frontend_name
        : preset.frontend_name;
    return labels;
  }, {});

  // Get smart docking button configuration
  const dockButtonConfig = getDockButtonConfig(
    robotStatus,
    isDriveToDockinProgress,
    t,
  );
  return (
    <Box
      sx={{
        ...sidebarStyles.container,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          {user.customerName}
        </Typography>

        <Button
          size="small"
          onClick={toggleLanguage}
          startIcon={<Language />}
          variant="outlined"
          sx={sidebarStyles.languageButton}
        >
          {language === "en" ? "🇬🇧 EN" : "🇩🇰 DA"}
        </Button>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Button
          variant={dockButtonConfig.variant}
          color={dockButtonConfig.color}
          fullWidth
          disabled={dockButtonConfig.disabled}
          startIcon={<EvStationIcon />}
          onClick={() => {
            if (dockButtonConfig.action === "cancel") {
              handleCancelDocking?.();
            } else {
              handleDriveToDock();
            }
          }}
          sx={{
            ...sidebarStyles.driveToDockButton,
            ...(dockButtonConfig.action === "cancel" && {
              backgroundColor: "warning.main",
              color: "warning.contrastText",
              "&:hover": {
                backgroundColor: "warning.dark",
              },
            }),
          }}
        >
          {dockButtonConfig.text}
        </Button>

        <Button
          variant="contained"
          fullWidth
          startIcon={<ContactSupport />}
          onClick={handleContactSupport}
          sx={{
            ...sidebarStyles.supportButton,
            ...(robotStatus.state === "error" && {
              ...sidebarStyles.supportButtonError,
              ...(!supportSentForCurrentError && {
                animation: "pulse 2s infinite",
              }),
            }),
            ...sidebarStyles.pulseAnimation,
          }}
        >
          {t("support")}
        </Button>

        {cleaningConfigPresent && cleaningPresets.length > 0 && (
          <Paper elevation={1} sx={{ p: 2 }}>
            {/* One stop per preset, in display order; names from the preset */}
            <CleaningLevelSlider
              label={t("cleaningLevelSlider.label")}
              value={presetPosition}
              onChange={(position) => {
                const preset = cleaningPresets[Number(position) - 1];
                if (preset) setCleaningLevel(Number(preset.cleaning_level));
              }}
              numColumns={cleaningPresets.length}
              color="heat"
              min={1}
              max={Math.max(cleaningPresets.length, 1)}
              valueLabelDisplay="off"
              intensityLabels={presetLabels}
            />
          </Paper>
        )}
      </Box>

      {/* Additional robot info */}
      <Paper elevation={1} sx={{ mt: 4, p: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="subtitle2">{t("robotStatus")}</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: sseConnected ? "#4caf50" : "#f44336",
                animation: sseConnected ? "none" : "pulse 1.5s infinite",
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {sseConnected ? "Live" : "Offline"}
            </Typography>
          </Box>
        </Box>
        {deviceStatus ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("operatingState")}:
              </Typography>
              <Typography variant="body2">
                {deviceStatus.operating_state === "docked"
                  ? deviceStatus.battery_level === 100
                    ? "Charged"
                    : "Charging"
                  : deviceStatus.operating_state || "Unknown"}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("battery")}:
              </Typography>
              <Typography variant="body2">
                {deviceStatus.battery_level || 0}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={deviceStatus.battery_level || 0}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {/* <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {robotStatus.currentzone}
            {robotStatus.state}
          </Typography>
        </Box> */}
      </Paper>

      {DISPLAY_GUIDE && (
        <Button
          variant="outlined"
          startIcon={<AutoStoriesIcon />}
          sx={{ mt: "auto" }}
          onClick={() => navigate(guidePath || "/guides")}
        >
          Guides
        </Button>
      )}
    </Box>
  );
};

export default ControlSidebar;
